import moment from "moment"

/**
 * Reading a notification feed: how old it is, which day it belongs to, and
 * which of them the filters leave.
 *
 * Age is carried as minutes rather than a timestamp so these stay pure — the
 * bell and the page render the same list, and a helper that reads the clock
 * would give two different answers on the same screen and be untestable
 * besides. The adapter converts the API's timestamp to minutes at the
 * boundary, in one place.
 */

const HOUR = 60
const DAY = 24 * HOUR

/** "now", "15m", "6h", "Yesterday", "15 days ago". */
export const relativeTime = (minutesAgo) => {
  const minutes = Math.max(0, Math.floor(Number(minutesAgo) || 0))

  if (minutes < 1) return "now"
  if (minutes < HOUR) return `${minutes}m`
  if (minutes < DAY) return `${Math.floor(minutes / HOUR)}h`
  if (minutes < 2 * DAY) return "Yesterday"
  return `${Math.floor(minutes / DAY)} days ago`
}

/**
 * The three headings the page groups under.
 *
 * "Earlier this month" is the design's own wording for the tail, and it is
 * left as the catch-all rather than being split further: the page loads more
 * on demand, so a fourth heading would appear only once someone had paged back
 * far enough for it to be wrong.
 */
export const DAY_GROUPS = ["Today", "Yesterday", "Earlier this month"]

export const dayGroupOf = (minutesAgo) => {
  const minutes = Math.max(0, Math.floor(Number(minutesAgo) || 0))
  if (minutes < DAY) return DAY_GROUPS[0]
  if (minutes < 2 * DAY) return DAY_GROUPS[1]
  return DAY_GROUPS[2]
}

/**
 * The list split under its headings, in the design's fixed order.
 *
 * A heading with nothing under it is dropped rather than shown empty — the
 * page is a feed, and "Yesterday" over a blank space reads as a loading
 * failure.
 */
export const groupByDay = (items = []) =>
  DAY_GROUPS.map((label) => ({
    label,
    items: items.filter((item) => dayGroupOf(item.minutesAgo) === label)
  })).filter((group) => group.items.length > 0)

/** Only notifications carrying a file — the "Downloads" tab. */
export const downloadsOf = (items = []) => items.filter((item) => item.file)

export const unreadCount = (items = []) => items.filter((item) => !item.read).length

/**
 * The calendar day a notification landed on, as `YYYY-MM-DD`.
 *
 * The one place the clock is consulted, and only because the date filter asks
 * a calendar question of a list that stores an age. `now` is a parameter so
 * the answer is pinnable rather than depending on when the test runs.
 *
 * Built from local parts rather than `toISOString`, which reports UTC and
 * would put an evening notification on tomorrow for anyone east of Greenwich —
 * this app runs in IST.
 */
export const dateOf = (minutesAgo, now = new Date()) => {
  const at = new Date(now.getTime() - Math.max(0, Number(minutesAgo) || 0) * 60000)
  const month = String(at.getMonth() + 1).padStart(2, "0")
  const day = String(at.getDate()).padStart(2, "0")
  return `${at.getFullYear()}-${month}-${day}`
}

/** Which row fields the search box looks at. */
const SEARCH_FIELDS = ["project", "title", "description"]

/** The calendar day of a date or date-time string. */
const dayOnly = (value) => String(value ?? "").slice(0, 10)

/**
 * The download states the "File Download Status" filter offers.
 *
 * Every status a file can actually be in, in the order a request passes
 * through them — a filter that offered a state the feed can never hold would
 * always come back empty.
 */
export const FILE_STATUS_OPTIONS = [
  { value: "queued", label: "In Queue" },
  { value: "downloading", label: "Downloading" },
  { value: "downloaded", label: "Downloaded" },
  { value: "failed", label: "Failed" }
]

/**
 * The periods the "Select Time Period" filter offers.
 *
 * `months` is how far back the window reaches; "Custom" has none, because it
 * is the one that hands the choice to the calendar instead.
 */
export const TIME_PERIOD_OPTIONS = [
  { value: "1m", label: "Last 1 Month", months: 1 },
  { value: "3m", label: "Last 3 Month", months: 3 },
  { value: "6m", label: "Last 6 Month", months: 6 },
  { value: "1y", label: "Last Year", months: 12 },
  { value: "custom", label: "Custom" }
]

/** The `{ start, end }` a preset stands for, as `YYYY-MM-DD`. */
export const rangeForPeriod = (value, now = new Date()) => {
  const option = TIME_PERIOD_OPTIONS.find((item) => item.value === value)
  if (!option?.months) return null
  const end = moment(now)
  return {
    start: end.clone().subtract(option.months, "months").format("YYYY-MM-DD"),
    end: end.format("YYYY-MM-DD")
  }
}

/** When a request was made, as the card prints it. */
export const requestedLabel = (at) => {
  const m = moment(at)
  return m.isValid() ? m.format("DD MMM YYYY, hh:mm A") : ""
}

/**
 * When the request was made.
 *
 * Requests raised in this session carry the moment they were lodged; the rest
 * of the feed carries an age, so the timestamp is read back out of it. Keeping
 * the fallback here is what lets the feed stay clock-free everywhere else.
 */
export const requestedAtOf = (item, now = Date.now()) =>
  item?.request?.at ?? now - Math.max(0, Number(item?.minutesAgo) || 0) * 60000

/**
 * The feed, narrowed by the page's controls.
 *
 * `fileStatus` only ever matches notifications that carry a file: filtering by
 * a download state and getting back rows with nothing to download would be a
 * filter that didn't filter.
 */
export const filterNotifications = (
  items = [],
  { project, user, range, fileStatus, search } = {},
  now = new Date()
) => {
  const term = String(search ?? "").trim().toLowerCase()

  return items.filter((item) => {
    if (project && item.project !== project) return false
    if (user && item.request?.user !== user) return false
    if (fileStatus && item.file?.status !== fileStatus) return false

    if (range?.start || range?.end) {
      // Both ends are compared as calendar days. `DatePicker` reports
      // "YYYY-MM-DD HH:mm:ss", and comparing that to a bare day string puts
      // the first day of the range *before* its own start.
      const day = dateOf(item.minutesAgo, now)
      if (range.start && day < dayOnly(range.start)) return false
      if (range.end && day > dayOnly(range.end)) return false
    }

    if (!term) return true

    const inFields = SEARCH_FIELDS.some((field) =>
      String(item[field] ?? "").toLowerCase().includes(term)
    )
    return (
      inFields ||
      String(item.file?.name ?? "").toLowerCase().includes(term) ||
      String(item.request?.user ?? "").toLowerCase().includes(term)
    )
  })
}

/** Distinct values of one field, as `Select` options, in first-seen order. */
const optionsFrom = (items, read) => {
  const seen = []
  items.forEach((item) => {
    const value = read(item)
    if (value && !seen.includes(value)) seen.push(value)
  })
  return seen.map((value) => ({ value, label: value }))
}

export const projectOptions = (items = []) => optionsFrom(items, (item) => item.project)

/**
 * Who has asked for a download, for the "Select user" filter.
 *
 * Only requesters: a notification that isn't a download request has no user to
 * name, so offering one would be a filter that matched nothing.
 */
export const userOptions = (items = []) => optionsFrom(items, (item) => item.request?.user)

/** How many the page shows before "Load More". */
export const PAGE_STEP = 6
